import { supabase } from '@/integrations/supabase/client';

/**
 * Exports reported to a partner-managed organisation's activity record
 * (log_member_activity, docs/partner-login.md). The database ignores the call
 * for anyone who is not an active member of such an organisation, so this is
 * fire-and-forget for every signed-in user and never touches the export.
 * Independent of analytics consent: it is part of the service the member
 * agreed to, not measurement.
 */
export type MemberActivityKind = 'export_pdf' | 'export_dxf' | 'export_bom' | 'export_image' | 'export_offer';

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function currentProjectId(): string | null {
  if (typeof window === 'undefined') return null;
  const id = new URLSearchParams(window.location.search).get('project');
  return id && UUID.test(id) ? id : null;
}

export function reportMemberActivity(kind: MemberActivityKind, detail: Record<string, string | number | boolean> = {}): void {
  if (typeof window === 'undefined') return;
  void (async () => {
    try {
      const { data } = await supabase.auth.getSession();
      if (!data.session) return;
      await supabase.rpc('log_member_activity' as never, { p_kind: kind, p_project: currentProjectId(), p_detail: detail } as never);
    } catch { /* never affects the user's export */ }
  })();
}

/** The activity kind behind an analytics event, if it is an export. */
export function memberActivityKindFor(eventName: string, params?: Record<string, string | number | boolean>): MemberActivityKind | null {
  if (eventName === 'pdf_exported') return 'export_pdf';
  if (eventName === 'feature_used') {
    if (params?.feature === 'dxf_export') return 'export_dxf';
    if (params?.feature === 'png_export') return 'export_image';
  }
  return null;
}
